import {
 useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { useRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';

import type { CustomUserPropertiesResponse, CustomUserProperty } from '../types';
import {
  buildCustomUserPropertyPredicateFilters,
  type CustomUserPropertyPredicateFilter,
} from '../utils/build-custom-user-property-predicate-filters';
import { useCustomUserPropertiesContextOptional }
from '../contexts/custom-user-properties-context';

const EMPTY_PROPERTIES: CustomUserProperty[] = [];
const EMPTY_PROPERTIES_BY_ID = new Map<string, CustomUserProperty>();
const EMPTY_FILTERS_MAP: Record<string, CustomUserPropertyPredicateFilter> = {};

function getEmptyUserPropertyPredicateFilters() {
  return {
    properties: EMPTY_PROPERTIES,
    propertiesById: EMPTY_PROPERTIES_BY_ID,
    filtersMap: EMPTY_FILTERS_MAP,
    loading: false,
    ready: false,
    error: null as Error | null,
  };
}

export function useOrganisationUserPropertyPredicateFilters(
  organisationId: string | undefined,
  enabled = true
) {
  const { t } = useTranslation();
  const shared = useCustomUserPropertiesContextOptional();
  const isEnabled = enabled && !!organisationId;

  const endpoint = useMemo(
    () => (organisationId ? `/v1/organisations/${organisationId}/user-properties` : ''),
    [organisationId]
  );

  const handleLoadError = useCallback(() => {
    AlertService.error(t('organisation:user_properties_load_error'));
  }, [t]);

  const shouldUseSharedContext = !!(
    isEnabled
    && shared
    && shared.enabled
    && shared.organisationId === organisationId
  );

  const {
    data: properties = [],
    loading,
    error,
    reset,
  } = useRequest<CustomUserPropertiesResponse>(endpoint, {
    enabled: isEnabled && !shouldUseSharedContext,
    onError: handleLoadError,
  });

  const sawLoadingRef = useRef(false);
  const previousOrganisationIdRef = useRef<string | undefined>();
  const [localReady, setLocalReady] = useState(false);

  useEffect(() => {
    if (!isEnabled || shouldUseSharedContext) return;

    const previousOrganisationId = previousOrganisationIdRef.current;
    const organisationChanged = (
      !!previousOrganisationId && previousOrganisationId !== organisationId
    );
    previousOrganisationIdRef.current = organisationId;

    // reset() clears the list before the next request finishes. Drop readiness
    // in the same turn so callers do not treat that empty list as final.
    // Do not list `reset` in deps: its identity changes every render.
    if (organisationChanged) {
      sawLoadingRef.current = false;
      setLocalReady(false);
      reset();
      return;
    }

    if (loading) {
      sawLoadingRef.current = true;
      return;
    }

    if (sawLoadingRef.current) {
      setLocalReady(true);
    }
    // eslint-disable-next-line -- reset identity is unstable in useRequest
  }, [isEnabled, organisationId, shouldUseSharedContext, loading]);

  const localFiltersMap = useMemo(
    () => buildCustomUserPropertyPredicateFilters(properties),
    [properties]
  );

  const localPropertiesById = useMemo(
    () => new Map(properties.map((property) => [property.id, property])),
    [properties]
  );

  if (!isEnabled) {
    return getEmptyUserPropertyPredicateFilters();
  }

  if (shouldUseSharedContext && shared) {
    return {
      properties: shared.properties,
      propertiesById: shared.propertiesById,
      filtersMap: shared.filtersMap,
      loading: shared.loading,
      ready: shared.ready,
      error: shared.error,
    };
  }

  return {
    properties,
    propertiesById: localPropertiesById,
    filtersMap: localFiltersMap,
    loading,
    ready: localReady,
    error: (error as Error | null) ?? null,
  };
}
